import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Ban, ChevronDown, RotateCcw } from 'lucide-react';
import { useCapabilities } from '../../../../hooks/useCapabilities';
import { projectDocumentEditorPathFromLabel } from '../../../../utils/projectRoutes';

type WorkReportStatus = 'open' | 'billed' | 'cancelled';

type WorkReportItem = {
  id: string;
  report_date: string;
  document_reference: string;
  quantity_mode: 'absolute' | 'incremental';
  status: WorkReportStatus;
  billed_by_reference: string | null;
  employee_name: string | null;
  lines: Array<{ quantity: number; unit_price_vk: number }>;
};

type OfferingWorkReports = {
  offeringId: string;
  displayId: string | null;
  title: string | null;
  reports: WorkReportItem[];
  overReported: Array<{ offering_position_id: string; item_name: string; unit: string; offered: number; reported: number }>;
};

const eur = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
const qtyFormat = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 3 });

function formatDate(value: string | null | undefined): string {
  if (!value) return '—';
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('de-DE');
}

function reportValue(report: WorkReportItem): number {
  return report.lines.reduce((sum, l) => sum + (Number(l.quantity) || 0) * (Number(l.unit_price_vk) || 0), 0);
}

/** Gleiche Tonlogik wie die Rechnungslisten: nur Offenes bekommt Farbe. */
function statusTone(report: WorkReportItem): { label: string; text: string; dot: string } {
  if (report.status === 'billed') {
    return {
      label: report.billed_by_reference ? `abgerechnet · ${report.billed_by_reference}` : 'abgerechnet',
      text: 'text-app-text-subtle',
      dot: 'bg-app-text-subtle/50',
    };
  }
  if (report.status === 'cancelled') return { label: 'storniert', text: 'text-app-text-subtle', dot: 'bg-app-text-subtle/50' };
  return { label: 'nicht abgerechnet', text: 'text-amber-800', dot: 'bg-amber-500' };
}

function useLeadWorkReports(leadId: string) {
  const [data, setData] = React.useState<OfferingWorkReports[] | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [reloadToken, setReloadToken] = React.useState(0);
  const reload = React.useCallback(() => setReloadToken((t) => t + 1), []);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/leads/${encodeURIComponent(leadId)}/work-reports`, { credentials: 'include' });
        const json = (await res.json().catch(() => ({}))) as { error?: string; offerings?: OfferingWorkReports[] };
        if (!res.ok) throw new Error(json.error ?? 'Arbeitsberichte konnten nicht geladen werden.');
        if (!cancelled) {
          setData(json.offerings ?? []);
          setError(null);
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Arbeitsberichte konnten nicht geladen werden.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [leadId, reloadToken]);

  return { data, error, reload };
}

function OverReportedHint({ rows }: { rows: OfferingWorkReports['overReported'] }) {
  const [open, setOpen] = React.useState(false);
  if (!rows.length) return null;
  return (
    <div className="pt-1 text-[12px] text-amber-800">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 hover:underline"
        aria-expanded={open}
      >
        <AlertTriangle className="h-3 w-3" />
        {rows.length === 1 ? 'Mehrmenge bei 1 Position' : `Mehrmenge bei ${rows.length} Positionen`}
        <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <ul className="mt-1 space-y-0.5 text-app-text-muted">
          {rows.map((row) => (
            <li key={row.offering_position_id} className="flex items-start justify-between gap-2">
              <span className="min-w-0 break-words">{row.item_name}</span>
              <span className="shrink-0 tabular-nums">
                {qtyFormat.format(row.reported)} / {qtyFormat.format(row.offered)} {row.unit}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function OfferingGroup({
  leadId,
  group,
  showHeading,
  canEdit,
  onChanged,
}: {
  leadId: string;
  group: OfferingWorkReports;
  showHeading: boolean;
  canEdit: boolean;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const [busyId, setBusyId] = React.useState<string | null>(null);
  const [actionError, setActionError] = React.useState<string | null>(null);
  const openIds = group.reports.filter((r) => r.status === 'open').map((r) => r.id);

  const toggleCancelled = async (report: WorkReportItem) => {
    const action = report.status === 'open' ? 'cancel' : 'restore';
    setBusyId(report.id);
    setActionError(null);
    try {
      const res = await fetch(
        `/api/offerings/${encodeURIComponent(group.offeringId)}/work-reports/${encodeURIComponent(report.id)}/${action}`,
        { method: 'POST', credentials: 'include' },
      );
      const json = (await res.json().catch(() => ({}))) as { error?: string };
      if (!res.ok) throw new Error(json.error ?? 'Aktion fehlgeschlagen.');
      onChanged();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Aktion fehlgeschlagen.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {showHeading ? (
        <p className="truncate pt-1 text-[12px] font-medium text-app-text-muted">
          {[group.displayId, group.title].filter(Boolean).join(' · ') || 'Vorgang'}
        </p>
      ) : null}
      <ul>
        {group.reports.map((report) => {
          const tone = statusTone(report);
          const editable = canEdit && (report.status === 'open' || report.status === 'cancelled');
          return (
            <li key={report.id} className="group flex items-start justify-between gap-3 py-1.5">
              <div className="min-w-0 flex-1">
                <span
                  className={`block truncate text-[13px] ${report.status === 'cancelled' ? 'text-app-text-subtle line-through' : 'text-app-text'}`}
                >
                  {report.employee_name || 'Arbeitsbericht'}
                  {report.document_reference ? ` · ${report.document_reference}` : ''}
                </span>
                <span className="mt-0.5 flex flex-wrap items-center gap-x-1.5 text-[12px] text-app-text-subtle">
                  <span>{formatDate(report.report_date)}</span>
                  <span aria-hidden>·</span>
                  <span className={`inline-flex items-center gap-1 ${tone.text}`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${tone.dot}`} aria-hidden />
                    {tone.label}
                  </span>
                  {report.quantity_mode === 'absolute' && report.status !== 'cancelled' ? (
                    <>
                      <span aria-hidden>·</span>
                      <span title="Gesendet mit einer älteren App-Version: die Mengen gelten als Gesamtstand des Vorgangs, nicht als Tagesmenge.">
                        Gesamtstand
                      </span>
                    </>
                  ) : null}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-1.5 pt-0.5">
                {editable ? (
                  <button
                    type="button"
                    disabled={busyId === report.id}
                    onClick={() => void toggleCancelled(report)}
                    className="rounded p-0.5 text-app-text-subtle opacity-0 transition-opacity hover:text-pipedrive-blue focus:opacity-100 group-hover:opacity-100 disabled:opacity-50"
                    title={report.status === 'open' ? 'Arbeitsbericht stornieren' : 'Storno zurücknehmen'}
                    aria-label={report.status === 'open' ? 'Arbeitsbericht stornieren' : 'Storno zurücknehmen'}
                  >
                    {report.status === 'open' ? <Ban className="h-3.5 w-3.5" /> : <RotateCcw className="h-3.5 w-3.5" />}
                  </button>
                ) : null}
                <span
                  className={`text-[13px] tabular-nums ${report.status === 'cancelled' ? 'text-app-text-subtle line-through' : 'text-app-text'}`}
                >
                  {eur.format(reportValue(report))}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      <OverReportedHint rows={group.overReported} />
      {canEdit && openIds.length > 0 ? (
        <button
          type="button"
          onClick={() =>
            navigate(
              `${projectDocumentEditorPathFromLabel({ id: leadId }, 'Rechnung', group.offeringId)}&neu=1&workReports=${encodeURIComponent(openIds.join(','))}`,
            )
          }
          className="pt-1.5 text-[12px] font-medium text-app-text-subtle hover:text-pipedrive-blue"
        >
          {openIds.length === 1
            ? 'Rechnung aus diesem Bericht erstellen'
            : `Rechnung aus ${openIds.length} Berichten erstellen`}
        </button>
      ) : null}
      {actionError ? <p className="pt-1 text-[12px] text-rose-700">{actionError}</p> : null}
    </div>
  );
}

/** Arbeitsberichte des Projekts als Belege je Vorgang (Controlling-Tab). */
export function LeadWorkReportsCard({
  leadId,
  variant = 'card',
}: {
  leadId: string;
  variant?: 'card' | 'sidebar';
}) {
  const { can } = useCapabilities();
  const canEdit = can('web.projekte.edit');
  const { data, error, reload } = useLeadWorkReports(leadId);

  if (!data && !error) return null;
  if (data && data.length === 0) return null;

  const groups = data ?? [];
  const openValue = groups
    .flatMap((g) => g.reports)
    .filter((r) => r.status === 'open')
    .reduce((sum, r) => sum + reportValue(r), 0);

  return (
    <section
      className={
        variant === 'sidebar'
          ? 'border-t border-app-border py-3'
          : 'mt-3 rounded-xl border border-app-border bg-app-surface px-4 py-3'
      }
    >
      <header className="mb-1.5 flex items-center justify-between gap-2">
        <h2 className="min-w-0 truncate text-[13px] font-semibold text-app-text">Arbeitsberichte</h2>
        {openValue > 0 ? (
          <span className="shrink-0 text-[12px] text-app-text-subtle" title="Wert der noch nicht abgerechneten Berichte (VK netto)">
            noch abzurechnen{' '}
            <span className="text-[13px] font-semibold tabular-nums text-app-text">{eur.format(openValue)}</span>
          </span>
        ) : null}
      </header>
      {error ? <p className="py-1.5 text-[13px] text-rose-700">{error}</p> : null}
      {groups.map((group) => (
        <OfferingGroup
          key={group.offeringId}
          leadId={leadId}
          group={group}
          showHeading={groups.length > 1}
          canEdit={canEdit}
          onChanged={reload}
        />
      ))}
    </section>
  );
}
